import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Sparkles } from 'lucide-react';
import { cn } from 'lib/utils';
import Switch from 'components/ui/switch';
import { PremiumBadge } from 'components/entitlements/PremiumBadge';
import { FEATURES, useFeature } from 'modules/entitlements';

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/**
 * "Use AI to fix questions" for the file import. Off by default and never
 * remembered: every upload is a fresh choice. A plan without the feature sees
 * the premium badge (which opens the upgrade dialog) instead of a dead switch;
 * the server enforces the plan either way.
 */
export const ImportAiToggle: React.FC<Props> = ({ checked, onChange, disabled }) => {
  const { t } = useTranslation();
  const entitled = useFeature(FEATURES.AI_IMPORT_REPAIR);

  return (
    <div
      className={cn(
        'flex h-full flex-col gap-2.5 rounded-xl border p-4 transition-colors duration-200',
        entitled && checked ? 'border-primary-600/50 bg-primary/10' : 'border-border bg-card',
      )}
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-background">
          <Sparkles className="h-[18px] w-[18px]" />
        </span>
        <p className="min-w-0 flex-1 text-sm font-semibold leading-snug">{t('import_ai_label')}</p>
        {entitled ? (
          <Switch
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            ariaLabel={t('import_ai_label')}
            size="md"
          />
        ) : (
          // The badge sits flush with the card's right edge: a tooltip centered on it would hang past the
          // dialog and give the modal body a horizontal scrollbar, so it is right-aligned to the badge.
          <PremiumBadge
            featureKey={FEATURES.AI_IMPORT_REPAIR}
            tooltip={t('import_ai_premium_hint')}
            tooltipClassName="left-auto right-0 translate-x-0"
          />
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('import_ai_hint')}</p>
      <p className="mt-auto flex items-center gap-1.5 text-xs font-medium text-primary-700">
        <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
        {t('import_ai_review_note')}
      </p>
    </div>
  );
};

export default ImportAiToggle;
